import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Drawer,
  Group,
  MultiSelect,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import type { TaxGroupDto } from './types';
import { useCreateTaxGroup, useTaxes, useUpdateTaxGroup } from './taxesApi';

interface Props {
  opened: boolean;
  onClose: () => void;
  group: TaxGroupDto | null;
}

interface FormValues {
  code: string;
  nameAr: string;
  nameEn: string;
  taxIds: string[];
}

export function TaxGroupFormDrawer({ opened, onClose, group }: Props) {
  const { t, i18n } = useTranslation();
  const { data: taxes } = useTaxes();
  const create = useCreateTaxGroup();
  const update = useUpdateTaxGroup();
  const submitting = create.isPending || update.isPending;
  const editing = group !== null;
  const drawerPosition = i18n.language === 'ar' ? 'left' : 'right';

  const taxOptions = useMemo(
    () =>
      (taxes ?? []).map((tax) => ({
        value: tax.taxId,
        label: `${i18n.language === 'ar' ? tax.nameAr : (tax.nameEn ?? tax.nameAr)} (${formatRate(tax.rate)})`,
      })),
    [taxes, i18n.language],
  );

  const form = useForm<FormValues>({
    initialValues: { code: '', nameAr: '', nameEn: '', taxIds: [] },
    validate: {
      code: (v) => (v.trim() ? null : t('common.errors.required')),
      nameAr: (v) => (v.trim() ? null : t('common.errors.required')),
    },
  });

  useEffect(() => {
    if (!opened) return;
    form.setValues({
      code: group?.code ?? '',
      nameAr: group?.nameAr ?? '',
      nameEn: group?.nameEn ?? '',
      taxIds: group?.taxIds ?? [],
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, group]);

  const submit = async (values: FormValues) => {
    const payload = {
      code: values.code.trim(),
      nameAr: values.nameAr.trim(),
      nameEn: values.nameEn.trim() || null,
      taxIds: values.taxIds,
    };
    try {
      if (editing) {
        await update.mutateAsync({ id: group.taxGroupId, request: payload });
      } else {
        await create.mutateAsync(payload);
      }
      notifications.show({
        color: 'green',
        title: editing ? t('common.toasts.updated') : t('common.toasts.created'),
        message: payload.nameAr,
      });
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={editing ? t('taxGroups.form.editTitle') : t('taxGroups.form.createTitle')}
      position={drawerPosition}
      size="md"
      padding="lg"
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack gap="md">
          <TextInput
            label={t('taxGroups.form.codeLabel')}
            description={t('taxGroups.form.codeHelp')}
            placeholder="STANDARD"
            withAsterisk
            {...form.getInputProps('code')}
          />
          <TextInput
            label={t('taxGroups.form.nameArLabel')}
            placeholder={t('taxGroups.form.nameArPlaceholder')}
            withAsterisk
            {...form.getInputProps('nameAr')}
          />
          <TextInput
            label={t('taxGroups.form.nameEnLabel')}
            placeholder={t('taxGroups.form.nameEnPlaceholder')}
            {...form.getInputProps('nameEn')}
          />
          <MultiSelect
            label={t('taxGroups.form.taxesLabel')}
            description={t('taxGroups.form.taxesHelp')}
            placeholder={t('taxGroups.form.taxesPlaceholder')}
            data={taxOptions}
            searchable
            clearable
            value={form.values.taxIds}
            onChange={(v) => form.setFieldValue('taxIds', v)}
          />
          {taxOptions.length === 0 && (
            <Text fz="xs" c="var(--wheels-ink-muted)">
              {t('taxGroups.form.noTaxes')}
            </Text>
          )}

          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="default" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

function formatRate(rate: number): string {
  return `${rate}%`;
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
